<script>
import { VideoChatStateKeys } from '@/enums/VideoChatStateKeys'
import { debounce } from 'lodash'
import { mapFields } from '@/helpers'
import { mapState } from 'vuex'
import { realtimeApi } from '@/realtime/api'
import {
  subscribeLocationCounts,
  subscribeLocations,
  subscribeOnlinePresence,
  subscribeRoomPresence,
  subscribeUserChannel,
} from '@/realtime/channels'

// Attendees only need two numbers from the location map (people in the event,
// people in this room). The backend counts them every few seconds and
// broadcasts `LocationCounts` on a public channel; the first counts come with
// /event/constants. So attendees never fetch the map. Only staff dashboards
// need the whole map: they fetch it, refetch on each `LocationsChanged` ping
// (fired by the room-presence webhook on join/leave) and keep this slow poll.
const LOCATION_POLL_MS = 60000
// With no counts from /event/constants and none broadcast by then (a backend
// without the counts), attendees fall back to fetching the whole map.
const LOCATION_COUNTS_FALLBACK_MS = 10000
// Every client refetches after the same ping, so each waits its own random
// delay instead of all hitting the API in the same second.
const LOCATION_REFETCH_JITTER_MS = 10000
// Must exceed WorkerPresence::ONLINE_CACHE_SECONDS in the backend.
const ACTIVE_ROSTER_REFRESH_MS = 2500
const ACTIVE_ROSTER_JITTER_MS = 5000

export default {
  props: {
    slug: {
      type: [Number, String],
      default: 0,
    },
  },
  data: () => ({
    realtimeUnsubscribers: [],
    roomUnsubscribe: null,
    locationPollTimer: null,
    locationCountsFallbackTimer: null,
    usesLocationMap: false,
  }),
  computed: {
    ...mapFields({
      fields: ['users', 'usersLocation', 'locationCounts', 'globalChannels'],
      base: ['event', 'socket'],
      mutation: 'event/setSocket',
    }),

    ...mapFields({
      fields: ['active_chats', 'private_channels'],
      base: ['chat'],
      mutation: 'chat/setState',
    }),
    ...mapState({
      user: (state) => state.session.user,
      video_chat_status: (state) => state.chat.video_chat.status,
      showLoading: (state) => state.event.show.isLoading,
    }),
  },
  watch: {
    // EventContainer (and this component) persist across room navigation while the
    // route slug changes; follow the user into the new room's presence channel.
    slug(newSlug, oldSlug) {
      if (newSlug !== oldSlug && this.roomUnsubscribe) {
        this.joinRoomPresence(newSlug)
      }
    },
  },
  created() {
    // Coalesce joins and wait beyond the API's 2s presence cache, so the active
    // filter's refresh includes the new member instead of retaining an old page.
    // The random part spreads clients out: every one of them sees the same joins.
    this.refreshRosterActive = debounce(() => {
      if (this.$store.state.event.roster.filters.includes('active')) {
        this.$store.dispatch('event/fetchRosterFirstPage')
      }
    }, ACTIVE_ROSTER_REFRESH_MS + Math.random() * ACTIVE_ROSTER_JITTER_MS)
    // Coalesce location refetches when LocationsChanged pings arrive (push-driven).
    this.onLocationsChanged = debounce(
      () => this.fetchLocations(),
      1000 + Math.random() * LOCATION_REFETCH_JITTER_MS,
    )
  },
  async mounted() {
    await this.setupCloudflareRealtime()
  },
  beforeDestroy() {
    this.cleanupCloudflareRealtime()
  },
  methods: {
    async setupCloudflareRealtime() {
      // Modo privacidad y no es personal: solo se une a la presencia de la
      // sala, y anónimo (el backend publica id/sala/evento, sin nombre). Así el
      // admin lo cuenta como conectado —antes, con este modo, veía 0— sin que
      // el asistente reciba la lista global de conectados, ni sus chats con
      // otros, ni el mapa de ubicaciones: el backend le niega todo eso.
      if (!this.$store.getters['event/canSeeAttendees']) {
        this.joinRoomPresence(this.slug)
        return
      }

      // Only channels with Visibility = "Show" (is_private = false) belong in the
      // main-UI "Canales" list. Hidden ones (is_private = true) are reachable only
      // via click actions / content / rooms, which resolve them by id from
      // constants.channels directly — so we exclude them from the global list here.
      this.globalChannels = (
        this.$store.state.event.constants.channels || []
      ).filter((channel) => !channel.is_private)

      // La lista de chats se pide en paralelo y no frena las suscripciones:
      // antes se esperaba acá, y para el soporte (~1.000 chats) la presencia,
      // su canal y los conteos llegaban tarde; si el pedido fallaba, no se
      // suscribía nunca.
      realtimeApi
        .privateChannelParticipants()
        .then((participants) => {
          if (!this._isDestroyed) {
            this.applyPrivateParticipants(participants)
          }
        })
        .catch(() => {
          // Sin la lista el resto sigue conectado; los chats nuevos llegan
          // igual por ParticipantAdded.
        })

      this.realtimeUnsubscribers = [
        subscribeOnlinePresence({
          here: this.handleOnlinePresence,
          joining: (user) => this.handleOnlinePresenceJoin(user),
          leaving: (user) => this.handleOnlinePresenceLeave(user),
        }),
        subscribeUserChannel(this.user.uuid, {
          ParticipantAdded: (payload) => {
            if (payload.participants) {
              this.applyPrivateParticipants(payload.participants)
            }
          },
          CallSignal: this.handleUserSignaling,
        }),
        subscribeLocationCounts({
          LocationCounts: (payload) => this.applyLocationCounts(payload),
        }),
        // Ping to refetch the whole map, for those who use it.
        subscribeLocations({
          LocationsChanged: () => {
            if (this.usesLocationMap) {
              this.onLocationsChanged()
            }
          },
        }),
      ]
      // Join the room presence channel (membership only) so the Worker counts this
      // user in the global location map; follows room navigation via the slug watch.
      this.joinRoomPresence(this.slug)

      const seed = this.$store.state.event.constants.location_counts
      if (seed) {
        this.applyLocationCounts({ counts: seed })
      }
      if (this.$store.getters['event/isPrivileged']) {
        await this.useLocationMap()
      } else if (!seed) {
        this.locationCountsFallbackTimer = setTimeout(
          () => this.useLocationMap(),
          LOCATION_COUNTS_FALLBACK_MS,
        )
      }
    },
    // The whole cross-room map, from the Worker via Laravel: the staff dashboards
    // need it, and it is the fallback when no counts are pushed.
    async useLocationMap() {
      if (this.usesLocationMap) {
        return
      }
      this.usesLocationMap = true
      await this.fetchLocations()
      // Stopped while fetching (counts arrived, or the component was destroyed).
      if (!this.usesLocationMap) {
        return
      }
      this.locationPollTimer = setInterval(() => this.fetchLocations(), LOCATION_POLL_MS)
    },
    stopLocationMap() {
      this.usesLocationMap = false
      this.onLocationsChanged.cancel()
      if (this.locationPollTimer) {
        clearInterval(this.locationPollTimer)
        this.locationPollTimer = null
      }
    },
    applyLocationCounts(payload) {
      if (this.locationCountsFallbackTimer) {
        clearTimeout(this.locationCountsFallbackTimer)
        this.locationCountsFallbackTimer = null
      }
      this.locationCounts = (payload && payload.counts) || {}
      // Counts arrived after an attendee fell back to the map: stop fetching it.
      if (this.usesLocationMap && !this.$store.getters['event/isPrivileged']) {
        this.stopLocationMap()
      }
    },
    joinRoomPresence(slug) {
      this.leaveRoomPresence()
      this.roomUnsubscribe = subscribeRoomPresence(slug, {})
    },
    leaveRoomPresence() {
      if (this.roomUnsubscribe) {
        this.roomUnsubscribe()
        this.roomUnsubscribe = null
      }
    },
    async fetchLocations() {
      try {
        this.usersLocation = await realtimeApi.getLocations()
      } catch (e) {
        // Keep the last known map on transient errors.
      }
    },
    cleanupCloudflareRealtime() {
      this.refreshRosterActive.cancel()
      this.realtimeUnsubscribers.forEach((unsubscribe) => unsubscribe())
      this.realtimeUnsubscribers = []
      this.leaveRoomPresence()
      this.$store.commit('event/clearOnlinePresence')
      this.stopLocationMap()
      if (this.locationCountsFallbackTimer) {
        clearTimeout(this.locationCountsFallbackTimer)
        this.locationCountsFallbackTimer = null
      }
      // Counts belong to this connection; don't carry them into the next event.
      this.locationCounts = null
    },
    applyPrivateParticipants(participants) {
      this.active_chats = participants
        .filter((row) => row.message_count > 0)
        .map((row) => row.user.uuid)

      // Bucle en vez de reduce con spread: el spread copiaba el objeto entero
      // en cada paso (cuadrático), y esto corre al entrar y con cada
      // ParticipantAdded que llega por WebSocket.
      const channels = {}
      for (const row of participants) {
        channels[row.channel_id] = row.user.uuid
      }
      this.private_channels = channels

      // Seed socket.users with chat partners so ChatHome's active-chat list
      // renders without the full eager roster (caveat 1).
      this.$store.commit(
        'event/mergeUsers',
        participants.map((row) => row.user),
      )
    },
    handleOnlinePresence(users) {
      this.$store.commit('event/replaceOnlinePresenceSnapshot', users.map((row) => row.id))
      // A reconnect can introduce members that have no loaded row yet.
      this.refreshRosterActive()
    },
    handleOnlinePresenceJoin(user) {
      this.$store.commit('event/setUserOnlinePresence', { id: user.id, online: true })
      // No more full-roster refetch (users-list-plan.md §2.5/4.7): if the "active"
      // filter is on and this attendee isn't on a loaded page, refresh page 1.
      const roster = this.$store.state.event.roster
      if (
        roster.filters.includes('active') &&
        !roster.items.some((row) => String(row.id) === String(user.id))
      ) {
        this.refreshRosterActive()
      }
    },
    handleOnlinePresenceLeave(user) {
      this.$store.commit('event/setUserOnlinePresence', { id: user.id, online: false })
      this.refreshRosterActive()
    },
    handleUserSignaling(signal) {
      console.log('userSignaling')
      if (signal.status == VideoChatStateKeys.waiting) {
        if (
          [
            VideoChatStateKeys.on_going,
            VideoChatStateKeys.waiting,
            VideoChatStateKeys.ringing,
          ].includes(this.video_chat_status)
        ) {
          realtimeApi.signalUser({
            status: VideoChatStateKeys.busy,
            user_uuid: this.user.uuid,
            target_user_uuid: signal.uuid,
          })
          return
        }
        this.$store.dispatch('chat/isRinging', {
          body: {
            user_uuid: this.user.uuid,
            target_user_uuid: signal.uuid,
            // Caller identity from the signal so the ring resolves without the
            // full roster (users-list-plan.md §4.7a).
            caller: signal.caller,
            // Caller-authoritative room id (caveat: lockstep) — receiver uses it
            // verbatim instead of re-deriving.
            room_uuids: signal.room_uuids,
          },
        })
      }
      if (signal.status == VideoChatStateKeys.on_going) {
        this.$store.dispatch('chat/answerCall')
      }
      if (signal.status == VideoChatStateKeys.busy) {
        this.$store.dispatch('chat/targetBusy')
      }
      if (signal.status == VideoChatStateKeys.declined) {
        if (this.video_chat_status == VideoChatStateKeys.on_going) {
          this.$store.dispatch('chat/hungUp')
        }
        if (this.video_chat_status == VideoChatStateKeys.waiting) {
          this.$store.dispatch('chat/declined')
        }
      }
    },
  },
  render() {
    console.log('real time connected')
    return this.$slots.default
  },
}
</script>
